import { ValidationTypes } from "@enums/validation-types";
import { OrientationTypes } from "@enums/orientation-types";
import { AcuantDocumentProcessingValidationResult } from "@idw-types/common/acuant-document-processing-result";
import { ValidationResult } from "@idw-types/common/validation-result";
import { getImageOrientation } from "./image-util";
import { AuditLogLevel } from "@enums/audit-log-level";
import { APValidationLivenessAssessmentResult } from "@idw-types/requests/ap-validation-liveness-assessment-result";
import { APValidationLivenessAssessmentTypes } from "@enums/ap-validation-liveness-assessment-types";
import { AuditLogTypes } from "@constants/audit-log-types";
import { isNullOrUndefined } from "./object-util";

const minimumAuthIDResolutions = {
    portrait: {
        width: 480,
        height: 640,
    },
    landscape: {
        width: 640,
        height: 480,
    },
};

const detectPortraitOrientation = (htmlImage: HTMLImageElement): ValidationResult => {
    const isSquareImage = htmlImage.naturalHeight === htmlImage.naturalWidth;
    const imageOrientation = getImageOrientation(htmlImage);
    const isPortrait = imageOrientation === OrientationTypes.PORTRAIT || (imageOrientation === OrientationTypes.ANY && isSquareImage);
    return {
        type: ValidationTypes.PORTRAIT_ORIENTATION_DETECTION,
        isValid: isPortrait,
        inProgress: false,
        isRequired: false,
        isDisabled: false,
        errorMessage: null
    };
};

const detectLandscapeOrientation = (htmlImage: HTMLImageElement): ValidationResult => {
    const isSquareImage = htmlImage.naturalHeight === htmlImage.naturalWidth;
    const imageOrientation = getImageOrientation(htmlImage);
    const isLandscape = imageOrientation === OrientationTypes.LANDSCAPE || (imageOrientation === OrientationTypes.ANY && isSquareImage);
    return {
        type: ValidationTypes.LANDSCAPE_ORIENTATION_DETECTION,
        isValid: isLandscape,
        inProgress: false,
        isRequired: false,
        isDisabled: false,
        errorMessage: null
    };
};

const detectGoodResolution = (htmlImage: HTMLImageElement): ValidationResult => {
    const orientation = getImageOrientation(htmlImage);
    let isValid = true;
    let errorMessage = null;
    let errorMessageOptions;
    if (orientation === OrientationTypes.PORTRAIT || orientation === OrientationTypes.LANDSCAPE) {
        isValid =
            minimumAuthIDResolutions.portrait.width <= htmlImage.naturalWidth &&
            minimumAuthIDResolutions.portrait.height <= htmlImage.naturalHeight;

        if (!isValid) {
            errorMessage = "content.acuant.validation.invalidDPIMessage";
            errorMessageOptions = {
                currentWidth: htmlImage.naturalWidth,
                currentHeight: htmlImage.naturalHeight,
                minimumWidth: minimumAuthIDResolutions.landscape.width,
                minimumHeight: minimumAuthIDResolutions.landscape.height,
            };
        }
    }

    return {
        type: ValidationTypes.GOOD_RESOLUTION_DETECTION,
        isValid: isValid,
        inProgress: false,
        isRequired: true,
        isDisabled: false,
        errorMessage: errorMessage,
        errorMessageOptions: errorMessageOptions,
    };
};

const detectGlare = (result: number, threshold: number): ValidationResult => {
    if (isNullOrUndefined(result) || isNullOrUndefined(threshold)) {
        return {
            type: ValidationTypes.GLARE_DETECTION,
            isValid: false,
            inProgress: false,
            isRequired: false,
            errorMessage: "",
            isDisabled: true
        };
    }
    return {
        type: ValidationTypes.GLARE_DETECTION,
        isValid: result > threshold,
        inProgress: false,
        isRequired: true,
        errorMessage: "",
        isDisabled: false
    };
};

const detectSharpness = (result: number, threshold: number): ValidationResult => {
    if (!result || !threshold) {
        return {
            type: ValidationTypes.SHARPNESS_DETECTION,
            isValid: false,
            inProgress: false,
            isRequired: false,
            errorMessage: "",
            isDisabled: true
        };
    }
    return {
        type: ValidationTypes.SHARPNESS_DETECTION,
        isValid: result > threshold,
        inProgress: false,
        isRequired: true,
        errorMessage: "",
        isDisabled: false
    };
};

const detectLiveness = (livenessValidationResult: APValidationLivenessAssessmentResult): ValidationResult => {
    if (livenessValidationResult) {
        return {
            type: ValidationTypes.LIVENESS_DETECTION,
            isValid: livenessValidationResult.assessment === APValidationLivenessAssessmentTypes.Live,
            inProgress: false,
            isRequired: true,
            errorMessage: livenessValidationResult.errorMessage,
            isDisabled: false
        };
    } else {
        return {
            type: ValidationTypes.LIVENESS_DETECTION,
            isValid: true,
            inProgress: false,
            isRequired: false,
            errorMessage: "",
            isDisabled: true
        };
    }
};

const logValidationResult = (
    type: ValidationTypes,
    isValid: boolean,
    writeAuditLog: (message: string, logLevel: AuditLogLevel) => void
) => {
    switch (type) {
        case ValidationTypes.CARD_DETECTION:
            break;
        case ValidationTypes.PORTRAIT_ORIENTATION_DETECTION:
            if (isValid) {
                writeAuditLog(AuditLogTypes.DCUI_PORTRAIT_ORIENTATION_PASSED, AuditLogLevel.INFO);
            } else {
                writeAuditLog(AuditLogTypes.DCUI_PORTRAIT_ORIENTATION_FAILED, AuditLogLevel.ERROR);
            }
            break;
        case ValidationTypes.LANDSCAPE_ORIENTATION_DETECTION:
            if (isValid) {
                writeAuditLog(AuditLogTypes.DCUI_LANDSCAPE_ORIENTATION_PASSED, AuditLogLevel.INFO);
            } else {
                writeAuditLog(AuditLogTypes.DCUI_LANDSCAPE_ORIENTATION_FAILED, AuditLogLevel.ERROR);
            }
            break;
        case ValidationTypes.GOOD_RESOLUTION_DETECTION:
            if (isValid) {
                writeAuditLog(AuditLogTypes.DCUI_ACUANT_IMAGE_VALIDATION_NO_LOW_DPI_DETECTED, AuditLogLevel.INFO);
            } else {
                writeAuditLog(AuditLogTypes.DCUI_ACUANT_IMAGE_VALIDATION_LOW_DPI_DETECTED, AuditLogLevel.ERROR);
            }
            break;
        case ValidationTypes.GLARE_DETECTION:
            if (isValid) {
                writeAuditLog(AuditLogTypes.DCUI_ACUANT_IMAGE_VALIDATION_NO_GLARE_DETECTED, AuditLogLevel.INFO);
            } else {
                writeAuditLog(AuditLogTypes.DCUI_ACUANT_IMAGE_VALIDATION_GLARE_DETECTED, AuditLogLevel.ERROR);
            }
            break;
        case ValidationTypes.SHARPNESS_DETECTION:
            if (isValid) {
                writeAuditLog(AuditLogTypes.DCUI_ACUANT_IMAGE_VALIDATION_NO_BLUR_DETECTED, AuditLogLevel.INFO);
            } else {
                writeAuditLog(AuditLogTypes.DCUI_ACUANT_IMAGE_VALIDATION_BLUR_DETECTED, AuditLogLevel.ERROR);
            }
            break;
        case ValidationTypes.LIVENESS_DETECTION:
            if (isValid) {
                writeAuditLog(AuditLogTypes.DCUI_LIVENESS_CHECK_SUCCESS, AuditLogLevel.INFO)
            } else {
                writeAuditLog(AuditLogTypes.DCUI_LIVENESS_CHECK_FAILURE, AuditLogLevel.ERROR);
            }
            break;
    }
};

export const performValidationStep = async (
    type: ValidationTypes,
    htmlImage: HTMLImageElement,
    livenessValidationResult: APValidationLivenessAssessmentResult,
    acuantValidationResults: AcuantDocumentProcessingValidationResult,
    acuantThresholds: { glare: number; dpi: number; sharpness: number },
    writeAuditLog: (message: string) => void
): Promise<ValidationResult> => {
    let result: ValidationResult;
    switch (type) {
        case ValidationTypes.CARD_DETECTION:
            break;
        case ValidationTypes.PORTRAIT_ORIENTATION_DETECTION:
            result = detectPortraitOrientation(htmlImage)
            break;
        case ValidationTypes.LANDSCAPE_ORIENTATION_DETECTION:
            result = detectLandscapeOrientation(htmlImage);
            break;
        case ValidationTypes.GOOD_RESOLUTION_DETECTION:
            result = detectGoodResolution(htmlImage);
            break;
        case ValidationTypes.GLARE_DETECTION:
            result = detectGlare(acuantValidationResults?.glare, acuantThresholds?.glare);
            break;
        case ValidationTypes.SHARPNESS_DETECTION:
            result = detectSharpness(acuantValidationResults?.sharpness, acuantThresholds?.sharpness);
            break;
        case ValidationTypes.LIVENESS_DETECTION:
            result = detectLiveness(livenessValidationResult);
            break;
        default:
            result = {
                type: ValidationTypes.UNKNOWN,
                isValid: false,
                inProgress: false,
                isRequired: false,
                isDisabled: true,
                errorMessage: ""
            };
            break;
    }

    logValidationResult(type, result.isValid, writeAuditLog);

    return result;
};